// File: frontend/src/pages/parent/Notices.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';

const Notices = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch active school notices & announcements feed
   */
  const fetchNotices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/parent/notices');
      if (response.data?.success) {
        setNotices(response.data.data || []);
      } else {
        setError(response.data?.message || 'Failed to load school notices.');
      }
    } catch (err) {
      console.error('Error fetching notices:', err);
      setError(err.response?.data?.message || err.message || 'Error loading notices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/parent/dashboard" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">School Notices & Announcements</h1>
              <p className="text-xs text-slate-500">Official circulars, events, PTM & exam notifications</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {loading ? (
          <div className="p-12 bg-white rounded-2xl border border-slate-200 text-center text-slate-400 font-medium">
            Loading school announcements...
          </div>
        ) : notices.length === 0 ? (
          <div className="p-12 bg-white rounded-2xl border border-slate-200 text-center text-slate-400 font-medium">
            No school notices published at this time.
          </div>
        ) : (
          <div className="space-y-4">
            {notices.map((n) => (
              <div
                key={n.id}
                className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition space-y-3"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span>
                    <span className="font-bold text-slate-700 text-xs uppercase tracking-wider">{n.category || 'General'}</span>
                  </div>
                  <span className="text-xs font-mono text-slate-400">
                    Posted on: {new Date(n.date).toLocaleDateString()}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-slate-900 leading-tight">
                  {n.title}
                </h3>

                <p className="text-sm text-slate-600 leading-relaxed">
                  {n.description}
                </p>
              </div>
            ))}
          </div>
        )}

      </main>
    </div>
  );
};

export default Notices;
